import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import SpinnerLoader from "../../../components/common/SpinnerLoader"
import LegalDocumentCard from "../../../components/admin/legal-documents/LegalDocumentCard"
import LegalDocumentFilters from "../../../components/admin/legal-documents/LegalDocumentFilters"
import Header from "../../../components/admin/common/Header"

const LegalDocumentsDashboard = () => {
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    category: "",
    type: "",
    isActive: "",
    search: "",
    page: 1,
    limit: 12,
  })
  const [pagination, setPagination] = useState({})
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    drafts: 0,
    agreements: 0,
  })

  const authInfo = useSelector((state) => state.auth.authInfo)
  const userInfo = useSelector((state) => state.auth.userInfo)

  useEffect(() => {
    fetchDocuments()
    fetchStats()
  }, [filters])

  const fetchDocuments = async () => {
    try {
      setLoading(true)
      const queryParams = new URLSearchParams()

      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          queryParams.append(key, filters[key])
        }
      })

      const response = await axios.get(`admin/legaldocument/documents?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        setDocuments(response.data.data.documents)
        setPagination({
          totalPages: response.data.data.totalPages,
          currentPage: response.data.data.currentPage,
          hasNextPage: response.data.data.hasNextPage,
          hasPrevPage: response.data.data.hasPrevPage,
          totalDocuments: response.data.data.totalDocuments,
        })
      }
    } catch (error) {
      console.error("Error fetching documents:", error)
      toast.error("Failed to fetch legal documents")
    } finally {
      setLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await axios.get("admin/legaldocument/documents", {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        const allDocs = response.data.data.documents
        setStats({
          total: allDocs.length,
          active: allDocs.filter((doc) => doc.isActive).length,
          drafts: allDocs.filter((doc) => !doc.isActive).length,
          agreements: 0, // This would need a separate endpoint
        })
      }
    } catch (error) {
      console.error("Error fetching stats:", error)
    }
  }

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1,
    }))
  }

  const handlePageChange = (page) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Are you sure you want to deactivate this document?")) {
      return
    }

    try {
      const response = await axios.delete(`admin/legaldocument/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${authInfo.token}`,
        },
      })

      if (response.data.status) {
        toast.success("Document deactivated successfully")
        fetchDocuments()
        fetchStats()
      }
    } catch (error) {
      console.error("Error deleting document:", error)
      toast.error(error.response?.data?.message || "Failed to delete document")
    }
  }

  if (loading && documents.length === 0) {
    return <SpinnerLoader />
  }

  return (
    <>
    <Header />
    <div 
      style={{ 
        minHeight: "100vh", 
        overflowY: "auto", 
        padding: '18px 100px'
      }} 
      className="legal-documents-dashboard"
    >
      <style jsx>{`
        @media (max-width: 1200px) {
          .legal-documents-dashboard {
            padding: 18px 50px !important;
          }
        }
        
        @media (max-width: 992px) {
          .legal-documents-dashboard {
            padding: 18px 30px !important;
          }
        }
        
        @media (max-width: 768px) {
          .legal-documents-dashboard {
            padding: 15px 20px !important;
          }
          
          .dashboard-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 15px;
          }
          
          .dashboard-header .btn {
            width: 100%;
            justify-content: center;
          }
          
          .stats-card .card-body {
            padding: 1rem 0.75rem;
          }
          
          .stats-card h4 {
            font-size: 1.5rem;
          }
          
          .stats-card p {
            font-size: 0.875rem;
          }
          
          .stats-card .fa-2x {
            font-size: 1.5em !important;
          }
        }
        
        @media (max-width: 576px) {
          .legal-documents-dashboard {
            padding: 12px 15px !important;
          }
          
          .dashboard-header h2 {
            font-size: 1.5rem;
          }
          
          .dashboard-header p {
            font-size: 0.875rem;
          }
          
          .stats-card .card-body {
            padding: 0.75rem;
          }
          
          .stats-card h4 {
            font-size: 1.25rem;
          }
          
          .stats-card p {
            font-size: 0.8rem;
          }
          
          .stats-card .fa-2x {
            font-size: 1.25em !important;
          }
          
          .empty-state h4 {
            font-size: 1.25rem;
          }
          
          .empty-state p {
            font-size: 0.875rem;
          }
          
          .empty-state .fa-3x {
            font-size: 2em !important;
          }
          
          .pagination {
            flex-wrap: wrap;
            justify-content: center;
          }
          
          .pagination .page-item {
            margin: 2px;
          }
          
          .pagination .page-link {
            padding: 0.375rem 0.75rem;
            font-size: 0.875rem;
          }
        }
        
        @media (max-width: 480px) {
          .legal-documents-dashboard {
            padding: 10px 12px !important;
          }
          
          .pagination .page-link {
            padding: 0.25rem 0.5rem;
            font-size: 0.8rem;
          }
        }
      `}</style>
      
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 dashboard-header">
              <div>
                <h2 className="mb-1">Legal Documents Management</h2>
                <p className="text-muted">Create and manage legal content, terms, and IP agreements</p>
              </div>
              <Link to="/admin/legal-documents/create" className="btn btn-primary">
                {/* <i className="fas fa-plus me-2"></i> */}
                Create Document
              </Link>
            </div>

            {/* Stats Cards */}
            <div className="row mb-4">
              <div className="col-md-3 col-sm-6 mb-3 mb-md-0">
                <div className="card bg-primary text-white stats-card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4 className="mb-0">{stats.total}</h4>
                        <p className="mb-0">Total Documents</p>
                      </div>
                      <i className="fas fa-file-alt fa-2x opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-6 mb-3 mb-md-0">
                <div className="card bg-success text-white stats-card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4 className="mb-0">{stats.active}</h4>
                        <p className="mb-0">Active Documents</p>
                      </div>
                      <i className="fas fa-check-circle fa-2x opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-6 mb-3 mb-sm-0">
                <div className="card bg-warning text-white stats-card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4 className="mb-0">{stats.drafts}</h4>
                        <p className="mb-0">Draft Documents</p>
                      </div>
                      <i className="fas fa-edit fa-2x opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-sm-6">
                <div className="card bg-info text-white stats-card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h4 className="mb-0">{stats.agreements}</h4>
                        <p className="mb-0">User Agreements</p>
                      </div>
                      <i className="fas fa-handshake fa-2x opacity-75"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <LegalDocumentFilters filters={filters} onFilterChange={handleFilterChange} />

            {/* Documents Grid */}
            {loading ? (
              <div className="text-center py-5">
                <SpinnerLoader />
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-5 empty-state">
                <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                <h4>No Legal Documents Found</h4>
                <p className="text-muted">Create your first legal document to get started</p>
                <Link to="/admin/legal-documents/create" className="btn btn-primary">
                  Create Document
                </Link>
              </div>
            ) : (
              <>
                <div className="row">
                  {documents.map((document) => (
                    <div key={document._id} className="col-xl-4 col-lg-6 col-md-6 mb-4">
                      <LegalDocumentCard document={document} onDelete={handleDeleteDocument} />
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <nav aria-label="Documents pagination">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${!pagination.hasPrevPage ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={!pagination.hasPrevPage}
                        >
                          Previous
                        </button>
                      </li>

                      {[...Array(pagination.totalPages)].map((_, index) => (
                        <li
                          key={index + 1}
                          className={`page-item ${pagination.currentPage === index + 1 ? "active" : ""}`}
                        >
                          <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                            {index + 1}
                          </button>
                        </li>
                      ))}

                      <li className={`page-item ${!pagination.hasNextPage ? "disabled" : ""}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={!pagination.hasNextPage}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default LegalDocumentsDashboard